// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE CLIENT EMAIL SKELETON — Dan's first real request of the system, 3 August.
//
// He sent two voice notes (1:53 + 0:39) asking for a תשתית / שלד he can copy,
// paste and fill himself, with three parts:
//   1. a Hebrew market brief for a client who does not know Tel Aviv — cost per
//      meter, arnona including the hi-tech discount, management fees, "per what
//      I already told him in the last meeting";
//   2. FIVE property slots as bullets — numbered, size, location, חלוקה, dates;
//   3. a closing paragraph on his brokerage fee, one month, and the הזמנת
//      שירותי תיווך that rides on this email and the ones after it.
//
// EVERY NUMBER BELOW IS DAN'S OWN, from the 29 July transcript. Nothing is
// invented and nothing is looked up — this letter goes out under his name to a
// real prospect, so a market figure we sourced from anywhere else would be a
// liability dressed as helpfulness. Line refs are to agents/otto/meeting-02.
//
// THREE JUDGEMENT CALLS, all of them his doctrine rather than our taste:
//
//   · NO BUILDING NAME AND NO FLOOR in the property bullets. Note that his own
//     field list omitted them — numbered, size, location, layout, dates. That is
//     the vagueness ladder from meeting 2 already applied: "first we convey who
//     Moshe is without calling him Moshe." The identity IS the asset and it is
//     withheld until the client has confirmed.
//
//   · THE FEE BLOCK IS NOT AN ATTACHED CONTRACT. He asked for "a brokerage
//     services order attached", but in meeting 2 he was emphatic that sending a
//     form up front "scares clients away" and that insisting on it at Pisgot
//     "destroyed a lot". What he actually uses — and what saved him in the
//     Trulion deal — is an email listing the properties that asks the client to
//     confirm by reply: "חוקית, זה תופס לכל דבר ועניין" (34:47). So the block
//     carries everything the form would carry, in a paragraph that does not
//     frighten: one month plus VAT · only on a closed deal · valid six months
//     (35:45–36:26) · reply to confirm. If he wants a real attachment instead,
//     it is one line to change.
//
//   · TWO OPTIONAL LINES, marked as such: furnished (the client asked for it)
//     and price (her actual question was "נשמח להבין את הסדר גודל מחירים").
//     Everything else is exactly his five fields. Delete them and the skeleton
//     is still his spec.
//
// Owner: Otto.
// ─────────────────────────────────────────────────────────────────────────────
import { useRef, useState } from "react";

// ── COPY, WITH A FALLBACK AND A VISIBLE FAILURE ──────────────────────────────
// The obvious `navigator.clipboard.writeText(t).then(...)` has TWO holes, and
// both only showed up by actually pressing the button rather than reading it:
//
//   1. It can REJECT — no clipboard permission, the document not focused, a
//      non-secure context. With no catch, nothing happens and the label never
//      changes: the button looks broken and gives no clue why.
//   2. Worse, it can simply NEVER SETTLE. While Chrome is sitting on a pending
//      clipboard permission decision the promise stays forever pending, so the
//      await never returns and no catch can help. That is the one that had me
//      chasing a "missed click" for twenty minutes — the click landed fine.
//
// So: race the async API against a short timeout, fall back to a hidden
// textarea and execCommand (needs no permission, only a user gesture), and if
// both fail, select the text in the panel so ⌘C still gets him there and say
// so on the button. A copy button on a letter he is about to send has to
// finish, one way or another, and never sit silent.
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      const ok = await Promise.race([
        navigator.clipboard.writeText(text).then(() => true),
        new Promise<boolean>((res) => setTimeout(() => res(false), 1200)),
      ]);
      if (ok) return true;
    }
  } catch {
    /* fall through to the textarea */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const C = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", hair: "#E3DED4", paper: "#FAF7F2",
  green: "#3F8F6F", plum: "#7A5B78", amber: "#B07A1E", red: "#C0492F",
  blue: "#2F6BB0", petrol: "#2C7E9E",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// ── THE SKELETON ─────────────────────────────────────────────────────────────
// Hebrew only — no Latin words anywhere in the body. Not a style preference: a
// Latin word inside an RTL line gets reordered on screen and silently scrambles
// the sentence, which is how the meeting-2 recap header came out as
// "Falcon, 29, MyJarvis, July". One direction per line. Ranges are written
// "בין X ל-Y" rather than "X-Y" for the same reason — a hyphen between two
// numbers is a bidi coin-flip.
const SKELETON = `היי [שם],

תודה על השיחה ועל הפירוט. לפני שאעביר נכסים ספציפיים, ריכזתי כאן תמונת מצב קצרה על שוק המשרדים בתל אביב, כדי שכל הצעה שתקבלו תהיה ברורה וניתנת להשוואה. אחרי זה מפורטים הנכסים הרלוונטיים.


■ שוק המשרדים בתל אביב, בקצרה

שכר דירה
בשוק שבו אני עובד, תל אביב והבורסה ברמת גן, המחירים נעים בין 120 ל-180 שקל למ"ר לחודש. הממוצע הוא בין 140 ל-150. כל המחירים אינם כוללים מע"מ.

ברוטו מול נטו, הנקודה שהכי משפיעה על ההשוואה
שכר הדירה ודמי הניהול מחושבים תמיד על השטח ברוטו, כלומר כולל החלק היחסי בשטחים הציבוריים של הבניין. הארנונה, לעומת זאת, מחושבת תמיד על הנטו, השטח שבפועל עובדים בו.
ככל שהבניין גדול יותר ויש בו יותר שטחים ציבוריים, כך הנטו קטן ביחס לברוטו. לדוגמה: אלף מ"ר ברוטו במגדל גדול יכולים להיות כשש מאות מ"ר רצפה בפועל, בעוד שבבניין פרטי אותו גודל יניב בין שמונה מאות וחמישים לתשע מאות מ"ר.
לכן ההשוואה הנכונה בין שתי אופציות היא לפי השטח האפקטיבי ולפי העלות הכוללת, ולא לפי המחיר למ"ר בלבד.

הוצאות נלוות, בין 50 ל-60 שקל למ"ר לחודש
· דמי ניהול: ממוצע של כ-20 שקל למ"ר ברוטו. במגדלים חדשים ויוקרתיים עד 29 שקל, ובבניינים פשוטים יותר סביב 15. זו עלות קבועה בחוזה רב משתתפים, ואין עליה משא ומתן.
· ארנונה: כ-37 שקל למ"ר, ומחושבת על הנטו. חברות הייטק, בתי תוכנה וסייבר זכאיות להנחה של עד 50 אחוז. אני מגלם את ההנחה הזו כפוטנציאל, בלי להתחייב עליה מראש.
· חשבונות, כלומר חשמל ומים וכדומה: מקובל לחשב בין 10 ל-15 שקל למ"ר ברוטו.

שורה תחתונה
כלל האצבע שאני נותן ללקוחות: אם שכר הדירה הוא כ-140 שקל למ"ר, סדר הגודל של העלות הכוללת הוא כ-200 שקל למ"ר ברוטו לחודש, לפני מע"מ. את המספר הזה יש להכפיל בשטח הברוטו כדי לקבל את ההוצאה החודשית בפועל.

היכן יש משא ומתן ואיפה אין
על דמי הניהול ועל הארנונה אין משא ומתן. המקום היחיד שבו יש מרחב אמיתי הוא מול בעל הנכס, על שכר הדירה ועל תנאי העסקה. זה החלק שאני מוביל.


■ הנכסים

נכס 1
· גודל: [   ] מ"ר ברוטו, [   ] מ"ר נטו
· מיקום: [   ]
· חלוקה: [   ]
· מועד כניסה: [   ], גמישות: [   ]
· מרוהט: [   ]
· שכר דירה: [   ] שקל למ"ר, דמי ניהול: [   ] שקל למ"ר, לפני מע"מ

נכס 2
· גודל: [   ] מ"ר ברוטו, [   ] מ"ר נטו
· מיקום: [   ]
· חלוקה: [   ]
· מועד כניסה: [   ], גמישות: [   ]
· מרוהט: [   ]
· שכר דירה: [   ] שקל למ"ר, דמי ניהול: [   ] שקל למ"ר, לפני מע"מ

נכס 3
· גודל: [   ] מ"ר ברוטו, [   ] מ"ר נטו
· מיקום: [   ]
· חלוקה: [   ]
· מועד כניסה: [   ], גמישות: [   ]
· מרוהט: [   ]
· שכר דירה: [   ] שקל למ"ר, דמי ניהול: [   ] שקל למ"ר, לפני מע"מ

נכס 4
· גודל: [   ] מ"ר ברוטו, [   ] מ"ר נטו
· מיקום: [   ]
· חלוקה: [   ]
· מועד כניסה: [   ], גמישות: [   ]
· מרוהט: [   ]
· שכר דירה: [   ] שקל למ"ר, דמי ניהול: [   ] שקל למ"ר, לפני מע"מ

נכס 5
· גודל: [   ] מ"ר ברוטו, [   ] מ"ר נטו
· מיקום: [   ]
· חלוקה: [   ]
· מועד כניסה: [   ], גמישות: [   ]
· מרוהט: [   ]
· שכר דירה: [   ] שקל למ"ר, דמי ניהול: [   ] שקל למ"ר, לפני מע"מ


■ דמי תיווך ואישור

כפי שציינתי מראש, דמי התיווך הם בגובה שכר דירה של חודש אחד בתוספת מע"מ, ומשולמים אך ורק במקרה שבו נחתמה עסקה בפועל.

הנכסים המפורטים במייל זה הועברו אליכם על ידי, במסגרת שירותי התיווך שאני נותן. אשמח שתשיבו במייל חוזר ותאשרו שאלו אכן נכסים שהוצגו והוכרו לכם דרכי. אישור בהודעה חוזרת מהווה הזמנת שירותי תיווך לכל דבר ועניין, והוא תקף לשישה חודשים ממועד משלוח המייל.

אני זמין לכל שאלה, ואשמח לתאם סיור בנכסים שיעניינו אתכם.

בברכה,
דן שיקה
פלקון נדל"ן
[טלפון]
[כתובת מייל]
[מספר רישיון מתווך]`;

// ── THE PERSONALISED OPENER ──────────────────────────────────────────────────
// Pastes ABOVE the market section instead of duplicating the whole letter.
// Built from her own WhatsApp brief (14:48 and 14:56): one year with expected
// growth, Carlebach light rail or Azrieli Sarona, prefers furnished, open plan
// with a small meeting room, both options fine but towers preferred, and the
// line that triggered all of this — "נשמח להבין את הסדר גודל מחירים".
//
// The towers paragraph is the point of the whole letter. She said she prefers
// towers; his own numbers say towers have the worst net-to-gross. Telling her
// that, unprompted, is what makes him the adviser rather than a man forwarding
// listings — requirement 5 ("let me lead the process") in a live case.
const OPENER = `היי רוני,

תודה על הפירוט, זה בדיוק מה שצריך כדי להתחיל.

לפני שאעביר נכסים ספציפיים, ריכזתי כאן תמונת מצב על שוק המשרדים בתל אביב. ביקשת להבין סדר גודל של מחירים, ומכיוון שאתם לא בארץ כרגע חשוב לי שתקבלו את התמונה המלאה ולא מספר בודד, כי המחיר למ"ר לבדו יכול להטעות.

סיכמתי לעצמי את מה שהעברת: אופק של שנה עם ציפייה לגדול ולעבור בהמשך למשרד גדול יותר, מיקום מועדף באזור קרליבך והרכבת הקלה או עזריאלי שרונה והסביבה, העדפה למשרד מרוהט, וחלוקה של חלל פתוח עם חדר ישיבות קטן. ציינת ששתי האופציות מקובלות עליכם ושאתם מעדיפים מגדלים.

הערה מקצועית אחת לגבי מגדלים, לפני שנתקדם. מגדלים הם בדרך כלל החוויה הנוחה יותר, אבל הם גם אלה שבהם היחס בין הברוטו לנטו הוא הפחות יעיל, ולכן העלות בפועל למטר שעובדים בו גבוהה יותר. אני אביא לכם גם מגדלים וגם בניינים איכותיים עם משרדי הייטק, נשווה לפי השטח האפקטיבי והעלות הכוללת, ותחליטו מתוך תמונה מלאה.

ועוד נקודה שרלוונטית לכם במיוחד: מכיוון שמדובר בסטארט-אפ, שווה לשים לב להנחת הארנונה לחברות טכנולוגיה שמפורטת למטה. בשטח כזה זה הבדל משמעותי בהוצאה החודשית.

כדי לדייק את הנכסים שאעביר, אשמח לשני פרטים:
1. כמה מ"ר אתם צריכים, או לחלופין כמה אנשים אתם היום וכמה אתם צופים בעוד שנה. זה הפרמטר שהכול נגזר ממנו.
2. שם החברה, לצורך הפנייה לבעלי הנכסים ולחברות הניהול.`;

type Panel = { id: string; eyebrow: string; title: string; note: string; text: string; tone: string; width: number; maxH: number };

const PANELS: Panel[] = [
  {
    id: "skeleton", eyebrow: "Copy and fill", title: "השלד — the email skeleton", tone: C.green,
    width: 780, maxH: 900,
    note: "Exactly what Dan asked for: market brief, five property slots as bullets, fee paragraph at the bottom. Every figure is his own, from 29 July. He fills the brackets and sends.",
    text: SKELETON,
  },
  {
    id: "opener", eyebrow: "Paste above the market section", title: "פתיחה לרוני — the personalised opener", tone: C.petrol,
    width: 560, maxH: 470,
    note: "Written from her actual WhatsApp brief, so the letter answers what she asked rather than reading as a template. Includes the honest note that towers have the worst net-to-gross, and the two facts he still needs.",
    text: OPENER,
  },
];

function CopyPanel({ p }: { p: Panel }) {
  const [state, setState] = useState<"idle" | "copied" | "selected">("idle");
  const preRef = useRef<HTMLPreElement | null>(null);
  const copy = async () => {
    const ok = await copyText(p.text);
    if (!ok && preRef.current) {
      // Last resort: put the whole letter under the cursor so ⌘C still works.
      const range = document.createRange();
      range.selectNodeContents(preRef.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    setState(ok ? "copied" : "selected");
    setTimeout(() => setState("idle"), 2600);
  };
  const label = state === "copied" ? "Copied" : state === "selected" ? "Press ⌘C" : "Copy";
  return (
    <div data-zoom style={{
      width: p.width, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderTop: `4px solid ${p.tone}`, borderRadius: 18,
      boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 26, fontFamily: FONT,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.13em",
                        textTransform: "uppercase", color: p.tone }}>{p.eyebrow}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: C.ink, lineHeight: 1.22, marginTop: 4 }}>{p.title}</div>
        </div>
        {/* `nopan nodrag` + stopping pointerdown is what actually makes this
            clickable. Without them React Flow's pane handler captures the
            pointer on mousedown to start a pan, so pointerup never lands on the
            button and NO CLICK EVENT IS EVER FIRED. A programmatic .click()
            works fine, which is what made this so confusing: the handler is
            correct, the mouse just never reaches it. Same device SectionBox
            uses for its own label. */}
        <button
          className="nopan nodrag"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={copy}
          style={{
            fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: "#fff",
            background: state === "copied" ? C.petrol : state === "selected" ? C.amber : C.ink,
            border: "none", borderRadius: 999, padding: "8px 16px", cursor: "pointer",
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
          {label}
        </button>
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.5, color: C.faint }}>{p.note}</div>
      {/* `nowheel` is React Flow's own opt-out — without it the canvas swallows
          the wheel event to pan the board and this panel never scrolls (exactly
          what happened on the first pass of the meeting-2 recap). The
          stopPropagation is belt and braces for the drag-to-pan handler. */}
      <pre
        ref={preRef}
        className="nowheel"
        onWheel={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          margin: 0, direction: "rtl", textAlign: "right", whiteSpace: "pre-wrap",
          wordBreak: "break-word", fontFamily: FONT, fontSize: 14, lineHeight: 1.6,
          color: C.ink, background: C.paper, border: `1px solid ${C.hair}`,
          borderRadius: 12, padding: "18px 20px", userSelect: "text", maxHeight: p.maxH,
          overflowY: "auto", overscrollBehavior: "contain", cursor: "text",
        }}>{p.text}</pre>
    </div>
  );
}

type Note = { tag: string; tone: string; title: string; body: string };

const NOTES: Note[] = [
  { tag: "Judgement call", tone: C.red, title: "No building name, no floor",
    body: "His own field list left them out — numbered, size, location, layout, dates — and that is not an oversight, it is the vagueness ladder from meeting 2: <b>“first we convey who Moshe is without calling him Moshe.”</b> The identity triple (building → floor → size) is the asset, so it is withheld until the client has confirmed by reply. The same reason the letter carries no photos: <b>a tenant name in an image gives the building away.</b>" },
  { tag: "Judgement call", tone: C.amber, title: "The fee block is a paragraph, not an attachment",
    body: "He asked for “a brokerage services order attached”. But in meeting 2 he was emphatic that sending a form up front <b>“scares clients away”</b> and that insisting on it at Pisgot “destroyed a lot”. What he actually uses is <b>an email listing the properties asking the client to confirm by reply</b> — “חוקית, זה תופס לכל דבר ועניין”, and it is <b>what saved him in the Trulion deal.</b> So the block carries everything the form would (one month plus VAT · only on a closed deal · <b>valid six months</b> · reply to confirm) in language that does not frighten. A real attachment is one line to change." },
  { tag: "Confirm with Dan", tone: C.plum, title: "Three things before it goes out",
    body: "<b>1 · Size.</b> Nowhere in the screenshots, and his whole model is per-meter × gross — so the letter can give rates and a rule of thumb, not a budget. The opener asks for it. <b>2 · His licence number</b> for the signature: he taught us a valid licence and a paid annual fee are two of the three things required before “were you the effective cause” can even be asked, so it belongs in the footer. <b>3 · The two optional lines</b> (furnished, price) — furnished because she asked for it, price because her actual question was “נשמח להבין את הסדר גודל מחירים”. Delete them and it is exactly his five fields." },
  { tag: "Sourcing", tone: C.green, title: "Every number is his, and it reconciles",
    body: "Rent 120–180, average 140–150 · management ~20 gross (29 in the newest towers, 15 in simple buildings, zero negotiation) · arnona ~37 on the <b>net</b> while rent and management are on the <b>gross</b> · up to 50% arnona discount for software and cyber · bills 10–15 · a 1,000m gross floor giving ~600 usable in a big tower against 850–900 in a private building. His own bottom line closes it: <b>“rent at 140 means I tell the client roughly 200 per meter × the gross.”</b> 140 + 55 ≈ 195. <b>Nothing here was looked up</b> — this goes out under his name." },
];

function NoteCard({ n }: { n: Note }) {
  return (
    <div data-zoom style={{
      width: 560, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderTop: `4px solid ${n.tone}`, borderRadius: 18,
      boxShadow: "0 10px 30px rgba(27,33,30,0.05)", padding: 26, fontFamily: FONT,
      display: "flex", flexDirection: "column", gap: 9,
    }}>
      <div style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.13em",
                    textTransform: "uppercase", color: n.tone }}>{n.tag}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: C.ink, lineHeight: 1.22,
                    letterSpacing: "-0.01em" }}>{n.title}</div>
      <div style={{ fontSize: 14.5, lineHeight: 1.55, color: C.ink2 }}
           dangerouslySetInnerHTML={{ __html: n.body }} />
    </div>
  );
}

export function EmailTemplate() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontFamily: FONT, fontSize: 14.5, lineHeight: 1.55, color: C.ink2, maxWidth: 1400 }}>
        <b>Dan's first real request of the system, 3 August</b> — two voice notes asking for a <span dir="rtl">שלד</span> he can
        copy, paste and fill: a Hebrew market brief, five property slots as bullets, and the brokerage-fee close.
        He also sent screenshots of the live thread, so this is written for a real client — <b>Roni Rubinstein</b>,
        an office for her partner's startup, a year's horizon, Carlebach or Azrieli Sarona, furnished, open plan
        with a small meeting room, and the line that triggered it: <i><span dir="rtl">נשמח להבין את הסדר גודל מחירים</span></i>.
        <span style={{ color: C.faint }}> Every figure is his own from 29 July; the notes on the right say where each
        judgement call came from.</span>
      </div>
      <div style={{ display: "flex", gap: 26, alignItems: "flex-start" }}>
        {PANELS.map((p) => <CopyPanel key={p.id} p={p} />)}
        <div style={{ display: "grid", gridTemplateColumns: "560px", gap: 26 }}>
          {NOTES.map((n) => <NoteCard key={n.title} n={n} />)}
        </div>
      </div>
    </div>
  );
}
